import { useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import DataTable, { StatusPill, fmtCurrency, fmtDate } from "../components/DataTable";
import { apiCall } from "../auth/AuthContext";

const TONE = (v) => {
  const s = (v || "").toLowerCase();
  if (s === "paid") return "success";
  if (s === "sent" || s === "partial") return "warning";
  if (s === "overdue") return "danger";
  if (s === "void") return "neutral";
  return "info";
};

export default function AdminRevenue() {
  const [records, setRecords] = useState([]);
  const [clients, setClients] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [i, c] = await Promise.all([apiCall("/api/admin/invoices"), apiCall("/api/admin/clients")]);
        setRecords(i.records || []);
        setClients((c.records || []).length);
      } catch (e) { console.error("AdminRevenue load failed", e); }
      finally { setLoading(false); }
    })();
  }, []);

  const summary = useMemo(() => {
    const sumAmount = (preds) => records.filter(preds).reduce((s, r) => s + (r.Amount || 0), 0);
    const totalInvoiced = records.reduce((s, r) => s + (r.Amount || 0), 0);
    const totalCollected = records.filter((r) => (r.Status || "").toLowerCase() === "paid").reduce((s, r) => s + (r["Net Amount"] || 0), 0);
    const totalPending = sumAmount((r) => ["sent", "partial"].includes((r.Status || "").toLowerCase()));
    const totalOverdue = sumAmount((r) => (r.Status || "").toLowerCase() === "overdue");
    const avgPerClient = clients > 0 ? totalCollected / clients : 0;
    return { totalInvoiced, totalCollected, totalPending, totalOverdue, avgPerClient };
  }, [records, clients]);

  // Bar chart data: 12 months of current year
  const monthly = useMemo(() => {
    const year = new Date().getFullYear();
    const buckets = Array.from({ length: 12 }, (_, i) => ({ m: i, label: new Date(year, i, 1).toLocaleString("en", { month: "short" }), amount: 0 }));
    records.forEach((r) => {
      if ((r.Status || "").toLowerCase() !== "paid") return;
      const d = r["Date Paid"] || r["Invoice Date"];
      if (!d) return;
      const dt = new Date(d);
      if (dt.getFullYear() === year) buckets[dt.getMonth()].amount += r["Net Amount"] || 0;
    });
    return buckets;
  }, [records]);
  const maxMonthly = Math.max(1, ...monthly.map((m) => m.amount));

  const columns = [
    { key: "Invoice Number", label: "Invoice #" },
    { key: "Name", label: "Client", sortable: true },
    { key: "Service", label: "Service" },
    { key: "Amount", label: "Amount", fmt: fmtCurrency, sortable: true },
    { key: "Processing Fee", label: "Fee (3%)", fmt: fmtCurrency },
    { key: "Net Amount", label: "Net", fmt: fmtCurrency },
    { key: "Invoice Date", label: "Date", fmt: fmtDate, sortable: true },
    { key: "Status", label: "Status", fmt: (v) => <StatusPill value={v} tone={TONE(v)} /> },
  ];

  return (
    <AdminLayout title="REVENUE" kicker="Invoices & Cash Flow">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-jc-gold/15 mb-10">
        {[
          ["Total Invoiced", summary.totalInvoiced, "text-jc-white"],
          ["Total Collected", summary.totalCollected, "text-emerald-300"],
          ["Total Pending", summary.totalPending, "text-amber-300"],
          ["Total Overdue", summary.totalOverdue, "text-red-300"],
          ["Avg / Client", summary.avgPerClient, "text-jc-gold-light", "revenue-avg-per-client"],
        ].map(([k, v, color, tid]) => (
          <div key={k} data-testid={tid || `revenue-${k.toLowerCase().replace(/[^a-z]/g, "-")}`} className="bg-jc-black p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-jc-gold">{k}</div>
            <div className={`mt-3 font-display text-2xl md:text-3xl ${color}`}>{fmtCurrency(v)}</div>
          </div>
        ))}
      </div>

      {/* Monthly bar chart */}
      <div data-testid="revenue-monthly-chart" className="border border-jc-gold/20 p-6 mb-10">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl text-jc-white tracking-wide">MONTHLY REVENUE — {new Date().getFullYear()}</h3>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-jc-muted">Net (after 3% fee)</p>
        </div>
        <div className="flex items-end gap-2 h-44">
          {monthly.map((m) => (
            <div key={m.m} className="flex-1 flex flex-col items-center gap-2">
              <div className="w-full bg-jc-gold/15 relative" style={{ height: "100%" }}>
                <div className="absolute inset-x-0 bottom-0 bg-jc-gold transition-all duration-700" style={{ height: `${(m.amount / maxMonthly) * 100}%` }} title={fmtCurrency(m.amount)} />
              </div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-jc-muted">{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      <DataTable
        testId="admin-revenue-table"
        loading={loading}
        records={records}
        columns={columns}
        searchKeys={["Invoice Number", "Name", "Service", "Status"]}
        emptyText="No invoices yet."
        initialSort={{ key: "Invoice Date", dir: "desc" }}
      />
    </AdminLayout>
  );
}
